import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useSalesContacts } from '../hooks/useSalesContacts';
import { useContactFiltering, type ContactSort } from '../hooks/useContactFiltering';
import { ContactFormModal } from '../components/ContactFormModal';
import {
  BUILD_STATUS_LABELS,
  SOURCE_LABELS,
  STAGE_CLASSES,
  STAGE_LABELS,
  SalesContactBuildStatus,
  SalesContactSource,
  SalesContactStage,
  type SalesContactQuery,
} from '../types';

const inputClass =
  'px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none';

const PAGE_SIZE = 25;

const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

/**
 * CRM-lite contact list (PE-2159, TCS staff only).
 *
 * TWO filtering layers on purpose. The five enum filters and `q` are the ones
 * order-service's SalesContactQueryDto understands, so they go over the wire and re-key the
 * query. Search and sort are client-side, because `q` matches only name/businessName/email
 * and a sales rep also searches by Instagram handle and phone.
 *
 * Reveal is client-side too: SalesContactQueryDto has NO page/limit/cursor, so the server
 * hands back the whole filtered set in one response. `displayLimit` +
 * IntersectionObserver (the merchants-page pattern) keeps the DOM small without pretending
 * there is server pagination. Pagination.tsx (dead) and PaginationSection.tsx (server-paged)
 * are both wrong here.
 */
export const ContactsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.SALES_CONTACT_MANAGE);

  const [stage, setStage] = useState<'' | SalesContactStage>('');
  const [buildStatus, setBuildStatus] = useState<'' | SalesContactBuildStatus>('');
  const [source, setSource] = useState<'' | SalesContactSource>('');
  const [solutionInterest, setSolutionInterest] = useState('');
  const [assignedRepId, setAssignedRepId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<ContactSort>('recent');
  const [creating, setCreating] = useState(false);

  const query: SalesContactQuery = useMemo(
    () => ({
      stage: stage || undefined,
      buildStatus: buildStatus || undefined,
      source: source || undefined,
      solutionInterest: solutionInterest.trim() || undefined,
      assignedRepId: assignedRepId.trim() || undefined,
    }),
    [stage, buildStatus, source, solutionInterest, assignedRepId],
  );

  const { data: contacts = [], isLoading, error } = useSalesContacts(query);
  const filtered = useContactFiltering(contacts, searchTerm, sort);

  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const visible = filtered.slice(0, displayLimit);
  const hasMore = displayLimit < filtered.length;

  // Any change to the result set starts the reveal over, so a newly filtered list never
  // opens scrolled halfway down a previous one.
  useEffect(() => {
    setDisplayLimit(PAGE_SIZE);
  }, [filtered.length, searchTerm, sort]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setIsLoadingMore(true);
          setTimeout(() => {
            setDisplayLimit((prev) => prev + PAGE_SIZE);
            setIsLoadingMore(false);
          }, 300);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore]);

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} contact{filtered.length === 1 ? '' : 's'} — prospects, conversations
            in flight and the merchants they became.
          </p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)}>New contact</Button>}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <Input
          name="search"
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Name, business, email, @handle, phone"
          className="w-64"
        />
        <label className="block text-[14px] font-[600] text-foreground">
          Stage
          <select
            className={`${inputClass} mt-1 block`}
            value={stage}
            onChange={(e) => setStage(e.target.value as '' | SalesContactStage)}
          >
            <option value="">All</option>
            {Object.entries(STAGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[14px] font-[600] text-foreground">
          Build
          <select
            className={`${inputClass} mt-1 block`}
            value={buildStatus}
            onChange={(e) => setBuildStatus(e.target.value as '' | SalesContactBuildStatus)}
          >
            <option value="">All</option>
            {Object.entries(BUILD_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[14px] font-[600] text-foreground">
          Source
          <select
            className={`${inputClass} mt-1 block`}
            value={source}
            onChange={(e) => setSource(e.target.value as '' | SalesContactSource)}
          >
            <option value="">All</option>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <Input
          name="solutionInterest"
          label="Solution interest"
          value={solutionInterest}
          onChange={(e) => setSolutionInterest(e.target.value)}
          placeholder="Rack & Box"
          className="w-44"
        />
        <Input
          name="assignedRepId"
          label="Assigned rep"
          value={assignedRepId}
          onChange={(e) => setAssignedRepId(e.target.value)}
          placeholder="Rep id"
          className="w-40"
        />
        <label className="block text-[14px] font-[600] text-foreground">
          Sort
          <select
            className={`${inputClass} mt-1 block`}
            value={sort}
            onChange={(e) => setSort(e.target.value as ContactSort)}
          >
            <option value="recent">Recently updated</option>
            <option value="name">Name</option>
            <option value="followers">Instagram followers</option>
          </select>
        </label>
      </div>

      {error && <p className="text-sm text-red-500">Could not load contacts.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && !error && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {contacts.length === 0 ? 'No contacts yet.' : 'No contacts match these filters.'}
        </p>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Business</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Stage</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Build</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Interest</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Updated</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                    className="border-b border-border hover:bg-muted cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium text-foreground">
                      {contact.name}
                      {contact.igHandle && (
                        <span className="block text-xs text-muted-foreground">
                          {contact.igHandle}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground">{contact.businessName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_CLASSES[contact.stage]}`}
                      >
                        {STAGE_LABELS[contact.stage]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {BUILD_STATUS_LABELS[contact.buildStatus]}
                    </td>
                    <td className="px-4 py-3 text-foreground">{contact.solutionInterest ?? '—'}</td>
                    <td className="px-4 py-3 text-foreground">{formatDate(contact.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div ref={sentinelRef} className="flex justify-center py-6 border-t border-border">
            {isLoadingMore && (
              <div className="w-6 h-6 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
            )}
            {!hasMore && (
              <span className="text-xs text-muted-foreground">
                Showing all {filtered.length} contacts
              </span>
            )}
          </div>
        </div>
      )}

      {creating && (
        <ContactFormModal
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/contacts/${id}`)}
        />
      )}
    </div>
  );
};
